import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './Marketing.css';

const API_ORIGIN =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

const LoginPage = () => {
  const navigate = useNavigate();

  // 'magic' | 'password'
  const [tab, setTab] = useState('magic');

  // Shared fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const switchTab = (t) => {
    setTab(t);
    setStatus({ type: '', message: '' });
    setPassword('');
  };

  // ── Magic Link ────────────────────────────────────────────────────────────
  const handleMagicLink = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: redirectTo },
      });
      if (error) {
        setStatus({ type: 'error', message: error.message || 'Failed to send magic link.' });
      } else {
        setStatus({
          type: 'success',
          message: '✉️ Magic link sent! Check your email and click the link to sign in.',
        });
      }
    } catch (err) {
      console.error('[MAGIC_LINK]', err);
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };


  // ── Password ──────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
      } else {
        setStatus({ type: 'error', message: data.error || 'Invalid email or password.' });
      }
    } catch (err) {
      console.error('[LOGIN]', err);
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const tabBase = {
    flex: 1,
    padding: '0.55rem 1rem',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
  };
  const tabActive = {
    ...tabBase,
    background: 'linear-gradient(135deg, #E66A20, #D45C1A)',
    color: '#fff',
    boxShadow: '0 2px 8px #D45C1A30',
  };
  const tabInactive = {
    ...tabBase,
    background: 'transparent',
    color: '#8892B0',
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '56px', height: '56px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #D45C1A22, #D45C1A44)',
            border: '1px solid #D45C1A44', marginBottom: '1rem',
          }}>
            <span style={{ fontSize: '24px' }}>🔒</span>
          </div>
          <h2 className="login-title">Sign In</h2>
          <div style={{ marginTop: '0.5rem' }}>
            <img src="/text_logo_transparent.png" alt="RoofingAssist.AI" style={{ height: 28, display: 'block', margin: '0 auto' }} />
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{
          display: 'flex', gap: '4px', padding: '4px',
          background: '#fdfbf7', borderRadius: '10px',
          border: '1px solid #e2ddd3', marginBottom: '1.5rem',
        }}>
          <button style={tab === 'magic' ? tabActive : tabInactive} onClick={() => switchTab('magic')}>
            ✨ Magic Link
          </button>
          <button style={tab === 'password' ? tabActive : tabInactive} onClick={() => switchTab('password')}>
            🔑 Password
          </button>
        </div>

        {/* Status banner */}
        {status.message && (
          <div style={{
            padding: '0.9rem 1rem', borderRadius: '8px', marginBottom: '1.25rem',
            backgroundColor: status.type === 'success' ? 'rgba(100, 255, 218, 0.08)' : 'rgba(255, 100, 100, 0.08)',
            color: status.type === 'success' ? '#64FFDA' : '#FF6464',
            border: `1px solid ${status.type === 'success' ? '#64FFDA44' : '#FF646444'}`,
            fontSize: '0.875rem', textAlign: 'center', lineHeight: '1.6',
          }}>
            {status.message}
          </div>
        )}

        {/* ── MAGIC LINK FORM ── */}
        {tab === 'magic' && (
          <form onSubmit={handleMagicLink}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                required
                autoFocus
                disabled={loading || status.type === 'success'}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              style={{ width: '100%', marginTop: '1.5rem', opacity: (loading || status.type === 'success') ? 0.7 : 1, cursor: (loading || status.type === 'success') ? 'not-allowed' : 'pointer' }}
              disabled={loading || status.type === 'success'}
            >
              {loading ? 'Sending…' : 'Send Magic Link ✨'}
            </button>
            <p style={{ color: '#8892B099', fontSize: '0.78rem', textAlign: 'center', marginTop: '0.85rem', lineHeight: 1.6 }}>
              We'll email you a one-click sign-in link — no password needed.
            </p>
          </form>
        )}

        {/* ── PASSWORD FORM ── */}
        {tab === 'password' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                required
                autoFocus
                disabled={loading}
              />
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              style={{ width: '100%', marginTop: '1.5rem', opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign In →'}
            </button>
          </form>
        )}

        <div style={{
          marginTop: '1.5rem', paddingTop: '1.5rem',
          borderTop: '1px solid #1E3A5F33', textAlign: 'center',
          color: '#8892B0', fontSize: '0.8rem', lineHeight: '1.5',
        }}>
          Staff access via GoHighLevel is granted by your workspace owner.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
